import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, BACKEND_URL } from "../lib/config";
import { Trophy, FileText, Download, Eye, ExternalLink } from "lucide-react";

const LEAGUES = [
  { id: "main", label: "Serie A", color: "#00E5FF" },
  { id: "autumn", label: "Autumn League", color: "#F59E0B" },
  { id: "winter", label: "Winter League", color: "#60A5FA" },
  { id: "spring", label: "Spring League", color: "#A78BFA" },
  { id: "summer", label: "Summer League", color: "#FB923C" },
  { id: "champions", label: "Champions League", color: "#00E5FF" },
  { id: "winter_champ", label: "Campione d'Inverno", color: "#FBBF24" },
  { id: "highlander1", label: "Highlander 1", color: "#EF4444" },
  { id: "highlander2", label: "Highlander 2", color: "#EC4899" },
  { id: "return", label: "Fanta Partenope Return", color: "#10B981" },
];

function formatBytes(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

export default function Rankings() {
  const [available, setAvailable] = useState({});
  const [preview, setPreview] = useState(null); // { league_id, mime_type }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/rankings/pdf/list")
      .then((r) => {
        if (!alive) return;
        const map = {};
        (r.data || []).forEach((d) => { map[d.league_id] = d; });
        setAvailable(map);
      })
      .catch(() => { /* silent */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const dlUrl = (id) => `${BACKEND_URL}/api/rankings/pdf/${id}/download`;

  return (
    <section id="classifica" className="relative py-24 md:py-32 px-5 md:px-8" data-testid="rankings-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-xs tracking-[0.4em] uppercase text-fp-cyan font-heading font-semibold">
            Live · aggiornamenti continui
          </div>
          <h2 className="mt-4 font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tight">
            <span className="silver-gradient">Classifiche</span>{" "}
            <span className="cyan-gradient">Live</span>
          </h2>
          <p className="mt-4 text-white/60 text-sm md:text-base max-w-2xl mx-auto">
            Scarica o visualizza le classifiche ufficiali di ogni competizione, aggiornate dall'organizzazione durante la stagione.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-white/50 py-16">Caricamento...</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {LEAGUES.map((l, idx) => {
              const meta = available[l.id];
              const isPdf = meta?.mime_type === "application/pdf";
              return (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className={`relative p-6 rounded-3xl border transition-all ${
                    meta
                      ? "border-fp-cyan/30 bg-gradient-to-br from-fp-cyan/10 via-fp-surface/40 to-fp-surface/40 hover:border-fp-cyan/50 hover:shadow-[0_0_30px_-8px_rgba(0,229,255,0.4)]"
                      : "border-white/10 bg-fp-surface/40"
                  }`}
                  data-testid={`rankings-card-${l.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: `${l.color}20`,
                        border: `1px solid ${l.color}66`,
                        color: l.color,
                      }}
                    >
                      <Trophy size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading font-bold text-lg md:text-xl silver-gradient">{l.label}</h3>
                      {meta ? (
                        <div className="mt-1 flex items-center gap-2 text-xs text-white/50">
                          <FileText size={12} className="text-fp-cyan" />
                          <span>{formatBytes(meta.size_bytes)}</span>
                          <span>·</span>
                          <span className="uppercase">{(meta.mime_type || "").split("/")[1]}</span>
                        </div>
                      ) : (
                        <div className="mt-1 text-xs text-white/40">Classifica in arrivo</div>
                      )}
                      {meta?.description && (
                        <p className="mt-2 text-sm text-white/70 italic line-clamp-2">"{meta.description}"</p>
                      )}
                    </div>
                  </div>

                  {meta && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      <button
                        onClick={() => setPreview({ league_id: l.id, mime_type: meta.mime_type, label: l.label })}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-white/15 text-white/80 hover:border-fp-cyan hover:text-fp-cyan text-xs font-heading font-bold uppercase tracking-wider transition-all"
                        data-testid={`rankings-preview-${l.id}`}
                      >
                        <Eye size={13} /> Anteprima
                      </button>
                      <a
                        href={dlUrl(l.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-fp-cyan text-fp-bg font-heading font-bold text-xs uppercase tracking-wider hover:shadow-[0_0_15px_rgba(0,229,255,0.4)] transition-all"
                        data-testid={`rankings-download-${l.id}`}
                      >
                        <Download size={13} /> Scarica
                      </a>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {preview && (
          <div
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
            onClick={() => setPreview(null)}
            data-testid="rankings-preview-modal"
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-5xl h-[85vh] rounded-2xl overflow-hidden bg-fp-bg border border-white/10"
            >
              <div className="absolute top-3 right-3 z-10 flex gap-2">
                <a
                  href={dlUrl(preview.league_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-fp-cyan text-fp-bg text-xs font-heading font-bold uppercase tracking-wider"
                >
                  <ExternalLink size={14} /> Apri
                </a>
                <button
                  onClick={() => setPreview(null)}
                  className="px-3 py-2 rounded-lg bg-fp-surface/80 border border-white/10 text-white text-xs font-heading font-bold uppercase tracking-wider"
                >
                  Chiudi
                </button>
              </div>
              <div className="absolute top-3 left-3 z-10">
                <div className="px-3 py-2 rounded-lg bg-fp-surface/80 border border-white/10 text-white text-xs font-heading font-bold uppercase tracking-wider">
                  {preview.label}
                </div>
              </div>
              {preview.mime_type === "application/pdf" ? (
                <iframe src={dlUrl(preview.league_id)} title={preview.label} className="w-full h-full" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-black p-5">
                  <img src={dlUrl(preview.league_id)} alt={preview.label} className="max-w-full max-h-full object-contain" />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
